import React, { useState, useEffect, useRef } from 'react';

/**
 * ComboFiltro — stessa grafica di ComboTabellata ma per opzioni statiche.
 * Non effettua chiamate API e non ha la funzione "crea".
 * Usa value/onChange di tipo string per supportare sia enum string che ID numerici (come stringa).
 */

interface Item {
  id: string;
  nome: string;
}

interface ComboFiltroProps {
  label: string;
  value: string | null;
  onChange: (id: string | null) => void;
  items: Item[];
  disabled?: boolean;
}

export default function ComboFiltro({
  label,
  value,
  onChange,
  items,
  disabled = false,
}: ComboFiltroProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedItem = items.find(i => i.id === value) || null;

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = items.filter(i =>
    i.nome.toLowerCase().includes(query.toLowerCase())
  );

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSelect = (item: Item) => {
    onChange(item.id);
    setOpen(false);
    setQuery('');
  };

  const handleDeselect = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') { setOpen(false); setQuery(''); }
    if (e.key === 'Enter' && filtered.length === 1) {
      e.preventDefault();
      handleSelect(filtered[0]);
    }
  };

  return (
    <div className="combo-tabellata" ref={wrapperRef}>
      <div
        className={`combo-tabellata__trigger ${disabled ? 'combo-tabellata__trigger--disabled' : ''}`}
        onClick={handleOpen}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleOpen(); }}
      >
        {selectedItem ? (
          <span className="combo-tabellata__badge">
            {selectedItem.nome}
            {!disabled && (
              <button
                type="button"
                className="combo-tabellata__badge-remove"
                onClick={handleDeselect}
                title="Rimuovi"
              >
                ×
              </button>
            )}
          </span>
        ) : (
          <span className="combo-tabellata__placeholder">Seleziona {label.toLowerCase()}...</span>
        )}
        {!disabled && <span className="combo-tabellata__arrow">▾</span>}
      </div>

      {open && (
        <div className="combo-tabellata__dropdown">
          <div className="combo-tabellata__search">
            <input
              ref={inputRef}
              type="text"
              className="bop-input combo-tabellata__input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={`Filtra ${label.toLowerCase()}...`}
            />
          </div>
          <ul className="combo-tabellata__list">
            {filtered.length === 0 && (
              <li className="combo-tabellata__empty">Nessun risultato</li>
            )}
            {filtered.map(item => (
              <li
                key={item.id}
                className={`combo-tabellata__item ${item.id === value ? 'combo-tabellata__item--selected' : ''}`}
                onClick={() => handleSelect(item)}
              >
                {item.nome}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
